import { z } from "zod";
import * as Sentry from "@sentry/react";

import { LiveReviewDataSchema, type LiveReviewData } from "@common/schemas/live-review";
import {
  documentVisibility,
  startAgentTurnTrace,
  traceAgentStream,
} from "@common/sentry/agent-latency";

import { safeFetch, safeFetchJSON, safeFetchStreaming } from "./safe-fetch";
import { drainRepairMergeStream } from "./repair-merge-stream";
import { getEnv } from "./envManager";
import type { ActionOutcome, ApplyFailureReason } from "@common/constants/appliedActions";
import type { CommentPlacement } from "@common/types/issue";
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";
import type { ConflictParagraphRequest, ParagraphZoneChoices } from "./redline-bulk-apply-local";

export type Status = "DONE" | "DETECTION" | "QUEUED" | "REMEDY" | "ERROR" | "IN_PROGRESS" | "SKIPPED";

// ERROR has its own handling; today DONE is the backend's only non-error terminal state.
export function isReviewComplete(status: Status): boolean {
  return status === "DONE";
}

const DocumentBackupSchema = z.object({
  name: z.string(),
  size: z.number(),
  turn_id: z.string().nullable(),
  tool_call_id: z.string().nullable(),
  url: z.string(),
});
const DocumentBackupsResponseSchema = z.object({
  backups: z.array(DocumentBackupSchema),
});
export type DocumentBackup = z.infer<typeof DocumentBackupSchema>;

export const uploadDocumentBackup = async (
  authHeader: string,
  conversationId: string,
  docDocxBase64: string,
  checkpoint: { capturedAt: string, turnId: string | null, toolCallId: string | null },
): Promise<DocumentBackup[]> => {
  const urlBase = getEnv("API_BASE_URL");
  const data = await safeFetchJSON<unknown>(
    `${urlBase}/conversations/${conversationId}/document-backups`,
    {
      method: "PUT",
      headers: { Authorization: authHeader, "Content-Type": "application/json" },
      body: JSON.stringify({
        doc_docx_base64: docDocxBase64,
        captured_at: checkpoint.capturedAt,
        turn_id: checkpoint.turnId,
        tool_call_id: checkpoint.toolCallId,
      }),
    },
  );
  return DocumentBackupsResponseSchema.parse(data).backups;
};

export const listDocumentBackups = async (
  authHeader: string,
  conversationId: string,
): Promise<DocumentBackup[]> => {
  const urlBase = getEnv("API_BASE_URL");
  const data = await safeFetchJSON<unknown>(
    `${urlBase}/conversations/${conversationId}/document-backups`,
    {
      method: "GET",
      headers: { Authorization: authHeader },
    },
  );
  return DocumentBackupsResponseSchema.parse(data).backups;
};

export const fetchReport = async (
  authHeader: string,
  reviewId: string
): Promise<{
  status: Status;
  report: string;
  originalFileUrl: string;
  reviewFileUrl: string;
  reportFileUrl: string;
  outlineFileUrl: string;
  errorMessage: string;
  validationMessage: string;
}> => {
  const urlBase = getEnv("API_BASE_URL");
  const data = await safeFetchJSON(`${urlBase}/document-review/${reviewId}`, {
    method: "GET",
    headers: { Authorization: authHeader },
  });

  if (!data) throw new Error("Failed to fetch the report.");

  return {
    status: data.status,
    report: data.report,
    originalFileUrl: data.original_file,
    reviewFileUrl: data.review_file,
    reportFileUrl: data.report_file,
    outlineFileUrl: data.outline_file,
    errorMessage: data.error,
    validationMessage: data.validation_message,
  };
};

export const fetchLiveReviewSnapshot = async (
  authHeader: string,
  reviewId: string,
): Promise<LiveReviewData | null> => {
  const urlBase = getEnv("API_BASE_URL");
  try {
    const data = await safeFetchJSON<unknown>(
      `${urlBase}/document-review/${reviewId}/live-snapshot`,
      {
        method: "GET",
        headers: { Authorization: authHeader },
      },
    );
    const parsed = LiveReviewDataSchema.safeParse(data);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

export const sendBase64ForReview = async (
  apiKey: string,
  fileName: string,
  base64Document: string,
  playbookIds: string[],
  reviewTrackedChanges: boolean,
  runChecksOnly: boolean,
  previousReviews: string[] = []
): Promise<string> => {
  const urlBase = getEnv("API_BASE_URL");
    const requestBody = JSON.stringify({
      file_name: fileName,
      file_base64: base64Document,
      playbook_ids: playbookIds,
      clean_rerun: getEnv("REVIEW_CLEAN_RERUN") ? (getEnv("REVIEW_CLEAN_RERUN") !== "false") : false,
      dry_run: getEnv("REVIEW_DRY_RUN") ? (getEnv("REVIEW_DRY_RUN") !== "false") : false,
      review_tracked_changes: reviewTrackedChanges,
      full_parallel: runChecksOnly,
      rule_names: getEnv("REVIEW_RULE_NAMES") ? getEnv("REVIEW_RULE_NAMES")!.split(",") : [],
      previous_reviews: previousReviews,
    });
    const data = await safeFetchJSON(`${urlBase}/document-review`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: apiKey,
      },
      body: requestBody,
    });

    if (!data) throw new Error("Failed to send document for review.");

    return data.review_id;
};

const REPORT_NOT_READY_STATUS = 404;
const POLL_INTERVAL_MS = 2000;
export const REPORT_POLL_TIMEOUT_MS = 120_000;

export const pollForReport = async (reportFileUrl: string, timeoutMs: number): Promise<any> => {
  const startTime = Date.now();

  while (Date.now() - startTime <= timeoutMs) {
    try {
      const response = await safeFetch(reportFileUrl);
      const data = await response.json();
      if (data.status === "ERROR") {
        throw new Error(data.error || "An error occurred while processing the request");
      }
      return data;
    } catch (error) {
      if ((error as any)?.status !== REPORT_NOT_READY_STATUS) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }
  }

  throw new Error(`Timeout: Report file not available after ${Math.round(timeoutMs / 1000)} seconds`);
};

import { buildClientContext } from "./client-context";
import { ProposalEngineAccess } from "./proposal-engine-access";
import { serializeAgentChatRequest, type FocusedDocumentAttachment } from "@common/types/agent-chat-request";
import type { DocumentChangesForAgent } from "@common/types/document-changes-for-agent";

export const agentChatSimple = async ({
  dioptraFetchStream,
  message,
  conversationId,
  modelName,
  skills,
  selectedTextSnippet,
  focusedDocument,
  documentChanges,
  agentInstructions,
}: {
  dioptraFetchStream: (url: string, options?: RequestInit) => Promise<Response>;
  message: string;
  conversationId: string;
  modelName: string;
  skills?: string[];
  selectedTextSnippet?: string;
  focusedDocument?: FocusedDocumentAttachment;
  documentChanges?: DocumentChangesForAgent;
  agentInstructions?: string;
}) => {
  const urlBase = getEnv("API_BASE_URL");

  const turn = startAgentTurnTrace((config) => Sentry.startInactiveSpan(config), {
    surface: "addin",
    mode: null,
    model: modelName,
    visibility: documentVisibility(),
  });

  try {
    const { authorName } = await ProposalEngineAccess.current();
    const response = await dioptraFetchStream(`${urlBase}/agent-chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
      },
      body: serializeAgentChatRequest({
        message,
        conversationId,
        modelName,
        userAgent: "word-addin",
        clientContext: buildClientContext(),
        skills,
        selectedTextSnippet,
        focusedDocument,
        documentChanges,
        agentInstructions,
        mode: null,
        author: authorName,
      }),
    });

    return traceAgentStream(turn, response.body as ReadableStream<Uint8Array>);
  } catch (error) {
    turn.finish("error");
    throw error;
  }
};

export const agentActionStream = async ({
  dioptraFetchStream,
  prompt,
  toolNames,
  modelName,
  selectedTextSnippet,
}: {
  dioptraFetchStream: (url: string, options?: RequestInit) => Promise<Response>;
  prompt: string;
  toolNames: string[];
  modelName?: string;
  selectedTextSnippet?: string;
}) => {
  const urlBase = getEnv("API_BASE_URL");

  const response = await dioptraFetchStream(`${urlBase}/agent-action`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "text/event-stream",
    },
    body: JSON.stringify({
      prompt,
      tool_names: toolNames,
      client_context: buildClientContext(),
      ...(modelName !== undefined && { model_name: modelName }),
      ...(selectedTextSnippet !== undefined && { selected_text_snippet: selectedTextSnippet }),
    }),
  });

  return response.body as ReadableStream<Uint8Array>;
};

export const regenerateRedline = async (
  authHeader: string,
  currentSectionOoxml: string,
  incomingSectionOoxml: string,
  originalSectionOoxml: string,
  regenerateContext: string,
  user_feedback: string,
  // Proposal engine only: the text card being regenerated, so the core can show
  // the model its previous attempt without parsing markup. Null for a markup card.
  previousTexts: RedlineTextProposal | null,
  reviewId?: string | null,
  ruleUuid?: string,
  actionPlanIndex?: number,
  stepIndex?: number,
): Promise<{ report_file: string }> => {
  const body: Record<string, unknown> = {
    current_section_ooxml: currentSectionOoxml,
    incoming_section_ooxml: incomingSectionOoxml,
    original_section_ooxml: originalSectionOoxml,
    regenerate_context: regenerateContext,
    user_feedback: user_feedback,
    previous_texts: previousTexts,
  };
  if (reviewId) {
    body.review_id = reviewId;
    body.rule_uuid = ruleUuid;
    body.action_plan_index = actionPlanIndex;
    body.step_index = stepIndex;
  }
  const response = await safeFetch(`${getEnv("API_BASE_URL")}/redline/regenerate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": authHeader,
    },
    body: JSON.stringify(body),
  });

  return response.json();
};

export interface RepairMergeRequest {
  failedOoxml: string;
  currentRangeOoxml: string;
  mergeStep: string;
  wordError: {
    code?: string;
    message?: string;
    location?: string;
  };
  attemptNumber: number;
  priorAttemptSummary?: string;
}

export interface RepairMergeResponse {
  repaired_ooxml: string;
  repair_summary: string;
}

export const repairMergeError = async (
  authHeader: string,
  payload: RepairMergeRequest,
): Promise<RepairMergeResponse> => {
  const response = await safeFetchStreaming(`${getEnv("API_BASE_URL")}/redline/repair-merge/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": authHeader,
    },
    body: JSON.stringify({
      failed_ooxml: payload.failedOoxml,
      current_range_ooxml: payload.currentRangeOoxml,
      merge_step: payload.mergeStep,
      word_error: payload.wordError,
      attempt_number: payload.attemptNumber,
      prior_attempt_summary: payload.priorAttemptSummary,
    }),
  });
  if (!response.body) {
    throw new Error("The repair stream response has no body.");
  }
  const result = await drainRepairMergeStream(response.body);
  return { repaired_ooxml: result.repaired_ooxml, repair_summary: result.repair_summary };
};

const bulkApplyResponseSchema = z.object({
  bulk_apply_status_file: z.string().min(1),
});

export const bulkApply = async (
  authHeader: string,
  reviewId: string,
  selectedRuleUuids: string[] = []
): Promise<string> => {
  const urlBase = getEnv("API_BASE_URL");
  const response = await safeFetch(`${urlBase}/document-review/${reviewId}/bulk-apply`, {
    method: "POST",
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      selected_rule_uuids: selectedRuleUuids
    }),
  });

  return bulkApplyResponseSchema.parse(await response.json()).bulk_apply_status_file;
};

export interface LiveReviewBulkApplyRemedy {
  rule_uuid: string;
  rule_name: string;
  target_bookmark_names: string[];
  target_ooxml_after: string[];
  /**
   * Proposal engine only: the card's text, when the review ran with the engine
   * on; the core writes it into the document through the web. Null for a
   * markup card, which is applied from `target_ooxml_after`.
   */
  target_texts: RedlineTextProposal | null;
  /**
   * Why the redline was proposed, for the zone choice when this card and another
   * change the same words: the card's instructions and context, or a playbook
   * rule and its reasoning. Empty for a markup card.
   */
  intent: string;
}

const zoneChoiceSchema = z.object({
  zone_label: z.string(),
  chosen_option_labels: z.array(z.string()),
  zone_text: z.string(),
  reason: z.string(),
});

const chooseConflictOptionsResponseSchema = z.object({
  paragraphs: z.array(z.object({ paragraph_label: z.string(), zones: z.array(zoneChoiceSchema) })),
});

export const chooseConflictOptions = async (authHeader: string, paragraphs: ConflictParagraphRequest[]): Promise<ParagraphZoneChoices[]> => {
  const urlBase = getEnv("API_BASE_URL");
  const response = await safeFetch(`${urlBase}/live-review/choose-conflict-options`, {
    method: "POST",
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ paragraphs }),
  });
  return chooseConflictOptionsResponseSchema.parse(await response.json()).paragraphs;
};

/** One redline of a paragraph as the comment pairing names it: a label ("R1"), the card's number, and its change as one line. */
export interface CommentPairingRedlineRequest {
  redline_label: string;
  card_label: string;
  change: string;
}

/** One comment of a paragraph as the comment pairing names it: a label ("C1"), the card's number, its text and the words it sits on. */
export interface CommentPairingCommentRequest {
  comment_label: string;
  card_label: string;
  text: string;
  anchor_text: string | null;
}

/** One paragraph with the redlines and comments proposed on it; `paragraph_label` is the add-in's key and comes back unchanged. */
export interface CommentPairingParagraphRequest {
  paragraph_label: string;
  paragraph_text: string;
  redlines: CommentPairingRedlineRequest[];
  comments: CommentPairingCommentRequest[];
}

const commentPairingSchema = z.object({
  comment_label: z.string(),
  redline_label: z.string().nullable(),
  reason: z.string(),
});

const pairCommentsResponseSchema = z.object({
  paragraphs: z.array(z.object({ paragraph_label: z.string(), pairs: z.array(commentPairingSchema).nullable() })),
});

export type ParagraphCommentPairings = z.infer<typeof pairCommentsResponseSchema>["paragraphs"][number];

export const pairCommentsWithRedlines = async (authHeader: string, paragraphs: CommentPairingParagraphRequest[]): Promise<ParagraphCommentPairings[]> => {
  const urlBase = getEnv("API_BASE_URL");
  const response = await safeFetch(`${urlBase}/live-review/pair-comments`, {
    method: "POST",
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ paragraphs }),
  });
  return pairCommentsResponseSchema.parse(await response.json()).paragraphs;
};

/** One card's new paragraphs in a slot, and why the card was proposed. */
export interface SlotCardParagraphsRequest {
  paragraph_texts: string[];
  intent: string;
}

/** A card already ordered in the slot, with its stored rank. */
export interface SlotMemberRequest extends SlotCardParagraphsRequest {
  card_id: string;
  rank: number;
}

/** One card arriving in a slot: the anchor, the paragraph the document holds after it, the members in rank order, and the arriving card's paragraphs. */
export interface OrderInsertionRequest {
  conversation_id: string;
  card_id: string;
  anchor_bookmark: string;
  anchor_text: string;
  next_paragraph_text: string | null;
  members: SlotMemberRequest[];
  arriving: SlotCardParagraphsRequest;
}

const slotPlacementSchema = z.object({
  card_id: z.string(),
  anchor_bookmark: z.string(),
  rank: z.number(),
  reason: z.string(),
});

export type SlotPlacementResponse = z.infer<typeof slotPlacementSchema>;

export const orderInsertion = async (authHeader: string, request: OrderInsertionRequest): Promise<SlotPlacementResponse> => {
  const urlBase = getEnv("API_BASE_URL");
  const response = await safeFetch(`${urlBase}/live-review/order-insertion`, {
    method: "POST",
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(request),
  });
  return slotPlacementSchema.parse(await response.json());
};

export type RegeneratedCardKind = "comment" | "reply";

export interface RegenerateCardTextRequest {
  card_kind: RegeneratedCardKind;
  card_text: string;
  context_text: string;
  user_feedback: string;
}

const regenerateCardTextResponseSchema = z.object({ text: z.string().min(1) });

export const regenerateCardText = async (authHeader: string, cardTextRequest: RegenerateCardTextRequest): Promise<string> => {
  const response = await safeFetch(`${getEnv("API_BASE_URL")}/redline/regenerate-card-text`, {
    method: "POST",
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(cardTextRequest),
  });
  return regenerateCardTextResponseSchema.parse(await response.json()).text;
};

export interface LiveReviewBulkApplyComment {
  rule_uuid: string;
  rule_name: string;
  bookmark: string;
  text: string;
  placement: CommentPlacement | null;
  anchor_text: string | null;
  issue_comment_id?: string;
  waits_for_rule_uuid?: string;
}

export const liveReviewBulkApply = async (
  authHeader: string,
  conversationId: string,
  toolCallId: string,
  remedies: LiveReviewBulkApplyRemedy[],
  comments: LiveReviewBulkApplyComment[],
  docDocxBase64: string
): Promise<string> => {
  const urlBase = getEnv("API_BASE_URL");
  const response = await safeFetch(`${urlBase}/live-review/bulk-apply`, {
    method: "POST",
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      conversation_id: conversationId,
      tool_call_id: toolCallId,
      remedies,
      comments,
      doc_docx_base64: docDocxBase64
    }),
  });

  return bulkApplyResponseSchema.parse(await response.json()).bulk_apply_status_file;
};

export const interruptAgentChat = async (
  authHeader: string,
  conversationId: string
): Promise<{ success: boolean; conversation_id: string; message: string }> => {
  const urlBase = getEnv("API_BASE_URL");
  const response = await safeFetch(`${urlBase}/agent-chat/interrupt`, {
    method: "POST",
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      conversation_id: conversationId
    }),
  });

  const data = await response.json();
  return data;
};

export const removeDocumentProtection = async (
  authHeader: string,
  documentBase64: string,
  documentName: string
): Promise<string> => {
  const urlBase = getEnv("API_BASE_URL");
  const response = await safeFetch(`${urlBase}/tools/remove-protection`, {
    method: "POST",
    headers: {
      Authorization: authHeader,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      document_base64: documentBase64,
      document_name: documentName
    }),
  });

  const data = await response.json();
  return data.unlocked_document_url;
};

// Zod validates these payloads at the network boundary: extra keys are
// stripped and nullable fields mirror the BE's JSON contract.
const projectSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish().transform((v) => v ?? null),
  aiInstructions: z.string().nullish().transform((v) => v ?? null),
  updatedAt: z.string().nullish().transform((v) => v ?? null),
  tags: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
});

export type IProject = z.infer<typeof projectSchema>;

const attachedProjectSchema = z.object({ id: z.string(), name: z.string() });

// These endpoints live on the WEBAPP server (WEBAPP_API_BASE_URL), not the
// agent BE — routing them through API_BASE_URL yields a CORS-rejected preflight.

export const listProjects = async (authHeader: string): Promise<IProject[]> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const data = await safeFetchJSON(`${urlBase}/api/projects?filter=granted`, {
    method: "GET",
    headers: { Authorization: authHeader },
  });
  const parsed = z.array(projectSchema).safeParse(data?.items);
  if (!parsed.success) return [];
  return parsed.data;
};

export const getConversationProject = async (
  authHeader: string,
  conversationId: string
): Promise<{ id: string; name: string } | null> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const data = await safeFetchJSON(`${urlBase}/api/chat/conversation/${conversationId}`, {
    method: "GET",
    headers: { Authorization: authHeader },
  });
  const parsed = attachedProjectSchema.safeParse(data?.project);
  return parsed.success ? parsed.data : null;
};

export const attachConversationToProject = async (
  authHeader: string,
  projectId: string,
  conversationId: string
): Promise<{ id: string; name: string }> => {
  const urlBase = getEnv("WEBAPP_API_BASE_URL");
  const data = await safeFetchJSON(`${urlBase}/api/chat/conversation/${conversationId}`, {
    method: "PATCH",
    headers: { Authorization: authHeader, "Content-Type": "application/json" },
    body: JSON.stringify({ projectId }),
  });
  const parsed = attachedProjectSchema.safeParse(data?.project);
  if (!parsed.success) throw new Error("Failed to attach conversation to project");
  return parsed.data;
};

export const recordActionOutcome = async (
  dioptraFetch: (url: string, options?: RequestInit) => Promise<unknown>,
  conversationId: string,
  toolCallId: string,
  actionId: string,
  outcome: ActionOutcome,
  failureReason?: ApplyFailureReason
): Promise<void> => {
  const urlBase = getEnv("API_BASE_URL");
  await dioptraFetch(`${urlBase}/conversations/${conversationId}/applied-actions`, {
    method: "POST",
    body: JSON.stringify({
      tool_call_id: toolCallId,
      action_id: actionId,
      outcome,
      failure_reason: failureReason ?? null,
      client_context: buildClientContext(),
    }),
  });
};
